import React, { ChangeEvent, Fragment, ReactElement, useEffect, useState } from "react";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead, TablePagination,
  TableRow,
  TableSortLabel, Tooltip
} from "@mui/material";
import { DateTime } from "luxon";
import { BaseType, IBasicEntity, IDelayQMOS, IDictionary, IParentSize } from "../../../types";
import {
  cloneObject, distinct,
  formatDate,
  formatDateTimeWithSeconds,
  formatNumber,
  stableSort
} from "../../../system/utils";
import EditDelayDialog from "./dialogs/editDelayDialog";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CutDelayDialog from "./dialogs/cutDelayDialog";
import { FilterList } from "@mui/icons-material";
import { useAsyncFetch } from "../../../system/useFetch";
import FilterDialog from "./dialogs/filterDialog";
import TableContainer from "../../../components/tableContainer";
import TablePaginationActions from "../../../components/tablePaginationActions";

type ColumnType = keyof IDelayQMOS | "factoryDay" | "shift" | "crew" | "machine" | "operationEdit";

interface IColumn {
  id: ColumnType;
  label: string;
  minWidth: (flag?: boolean) => number;
  width?: (flag?: boolean) => number;
  align?: "right";
  format?: (value: number) => string;
  filter?: boolean;
  prodField?: boolean;
}

type Order = "asc" | "desc";

const columns: IColumn[] = [
  {
    id: "operationEdit",
    label: "",
    minWidth: () => 90,
    width: () => 90,
  },
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: () => 100,
    width: () => 140,
    format: (value) => DateTime.fromSeconds(value).toLocaleString(DateTime.DATE_SHORT),
    prodField: true
  },
  {
    id: "shift",
    label: "Shift",
    minWidth: () => 80,
    width:() => 80,
    prodField: true
  },
  {
    id: "crew",
    label: "Crew",
    minWidth: () => 80,
    width: () => 80,
    prodField: true
  },
  {
    id: "machine",
    label: "Machine",
    minWidth: () => 100,
    width: () => 100,
    prodField: true
  },
  {
    id: "beginDatetime",
    label: "Begin",
    minWidth: () => 170,
    width: () => 190,
    format: (value) => formatDateTimeWithSeconds(value)
  },
  {
    id: "endDatetime",
    label: "End",
    minWidth: () => 170,
    width: () => 190,
    format: (value) => formatDateTimeWithSeconds(value)
  },
  {
    id: "duration",
    label: "Duration [min]",
    minWidth: () => 100,
    width: () => 100,
    align: "right"
  },
  {
    id: "areaGroup",
    label: "Area Group",
    minWidth: () => 150,
    filter: true
  },
  {
    id: "area",
    label: "Area",
    minWidth: () => 150,
    filter: true
  },
  {
    id: "equipment",
    label: "Equipment",
    minWidth: () => 150,
    filter: true
  },
  {
    id: "observation",
    label: "Observation",
    minWidth: () => 90,
    width: () => 90,
    filter: true
  },
  {
    id: "reason",
    label: "Reason",
    minWidth: () => 150,
    filter: true
  },
  {
    id: "actionCode",
    label: "Action",
    minWidth: () => 150,
    filter: true
  },
  {
    id: "responsibility",
    label: "Responsibility",
    minWidth: () => 90,
    width: () => 90,
    filter: true
  },
  {
    id: "comment",
    label: "Justification",
    minWidth: () => 150,
  },
  {
    id: "editedBy",
    label: "Edited By",
    minWidth: () => 150,
    width: () => 150,
  },
];

interface IInterruptionTableProps {
  data: IDelayQMOS[];
  selected: IDelayQMOS | null;
  setSelected: (row: IDelayQMOS) => void;
  onRefresh?: (delay: IDelayQMOS) => void;
  onRefreshAfterCut?: any;
  isEditable?: boolean | null;
  parentSize: IParentSize;
}

interface IDialogFilterData {
  key: keyof IFilter;
  title: string;
  open: boolean;
}

interface IColumnFilter {
  keys: string[];
  selected: string[];
}

interface IFilter {
  areaGroup: IColumnFilter;
  area: IColumnFilter;
  equipment: IColumnFilter;
  observation: IColumnFilter;
  reason: IColumnFilter;
  actionCode: IColumnFilter;
  responsibility: IColumnFilter;
}

const defaultFilter: IFilter = {
  areaGroup: { keys: [], selected: [] },
  area: { keys: [], selected: [] },
  equipment: { keys: [], selected: [] },
  observation: { keys: [], selected: [] },
  reason: { keys: [], selected: [] },
  actionCode: { keys: [], selected: [] },
  responsibility: { keys: [], selected: [] }
};

function descendingComparator(a: IDelayQMOS, b: IDelayQMOS, orderBy: ColumnType) {
  if (orderBy === "factoryDay") {
    if (a.production && b.production) {
      if (b.production.shiftHistory.factoryDay < a.production.shiftHistory.factoryDay) {
        return -1;
      }

      return b.production.shiftHistory.factoryDay > a.production.shiftHistory.factoryDay ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "shift") {
    if (a.production && b.production) {
      if (b.production.shiftHistory.name < a.production.shiftHistory.name) {
        return -1;
      }

      return b.production.shiftHistory.name > a.production.shiftHistory.name ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "crew") {
    if (a.production && b.production) {
      if (b.production.crew.name < a.production.crew.name) {
        return -1;
      }

      return b.production.crew.name > a.production.crew.name ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "machine") {
    if (a.production && b.production) {
      if (b.production.zone.name < a.production.zone.name) {
        return -1;
      }

      return b.production.zone.name > a.production.zone.name ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "beginDatetime") {
    if (b.beginDatetime < a.beginDatetime) {
      return -1;
    }

    return b.beginDatetime > a.beginDatetime ? 1 : 0;
  }

  if (orderBy === "endDatetime" && b.endDatetime && a.endDatetime) {
    if (b.endDatetime < a.endDatetime) {
      return -1;
    }

    return b.endDatetime > a.endDatetime ? 1 : 0;
  }

  if (orderBy === "duration" && b.duration && a.duration) {
    if (b.duration < a.duration) {
      return -1;
    }

    return b.duration > a.duration ? 1 : 0;
  }

  if (b[orderBy as keyof IDelayQMOS] || a[orderBy as keyof IDelayQMOS]) {
    const key = orderBy as keyof IDelayQMOS;
    const pathA = a[key] && (a[key] as IBasicEntity).name ? (a[key] as IBasicEntity).name : "";
    const pathB = b[key] && (b[key] as IBasicEntity).name ? (b[key] as IBasicEntity).name : "";

    if (pathB < pathA) {
      return -1;
    }

    return pathB > pathA ? 1 : 0;
  }

  return 0;
}


function getComparator<Key extends ColumnType>(
  order: Order,
  orderBy: Key
): (a: IDelayQMOS, b: IDelayQMOS) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const DelayTable = ({ data, selected, setSelected, onRefresh, onRefreshAfterCut, isEditable, parentSize }: IInterruptionTableProps): ReactElement => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<ColumnType>("beginDatetime");
  const [open, setOpen] = useState<boolean>(false);
  const [dialogFilterData, setDialogFilterData] = useState<IDialogFilterData>({ open: false, key: "areaGroup", title: "" });
  const [openCutDialog, setOpenCutDialog] = useState<boolean>(false);
  const [selectedNewRow, setSelectedNewRow] = useState({} as IDelayQMOS);
  const [updateDelay, { data: delayUpdated }] = useAsyncFetch<IDelayQMOS | null>();
  const [insNewDelay] = useAsyncFetch<IDelayQMOS>();
  // const [filter, setFilter] = useState<IDictionary<string[]> | null>(null);
  const [dataFiltered, setDataFiltered] = useState<IDelayQMOS[] | null>(null);
  const [filter, setFilter] = useState<IFilter>(defaultFilter);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableContainerHeight = parentSize.height - 50;

  const dataLength = data ? data.length : 0;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;

  useEffect(() => {

    const newFilter = cloneObject(defaultFilter);
    const undefinedValues: IDictionary<boolean> = {};

    data.forEach(delay => {

      if (delay.areaGroup) {
        newFilter.areaGroup.keys.push(delay.areaGroup.name);
      } else if(!undefinedValues.areaGroup) {
        newFilter.areaGroup.keys.push("UNDEFINED");
        undefinedValues.areaGroup = true;
      }

      if (delay.area) {
        newFilter.area.keys.push(delay.area.name);
      } else if(!undefinedValues.area) {
        newFilter.area.keys.push("UNDEFINED");
        undefinedValues.area = true;
      }

      if (delay.equipment) {
        newFilter.equipment.keys.push(delay.equipment.name);
      } else if(!undefinedValues.equipment) {
        newFilter.equipment.keys.push("UNDEFINED");
        undefinedValues.equipment = true;
      }

      if (delay.observation) {
        newFilter.observation.keys.push(delay.observation.name);
      } else if(!undefinedValues.observation) {
        newFilter.observation.keys.push("UNDEFINED");
        undefinedValues.observation = true;
      }

      if (delay.reason) {
        newFilter.reason.keys.push(delay.reason.name);
      } else if(!undefinedValues.reason) {
        newFilter.reason.keys.push("UNDEFINED");
        undefinedValues.reason = true;
      }

      if (delay.actionCode) {
        newFilter.actionCode.keys.push(delay.actionCode.name);
      } else if(!undefinedValues.actionCode) {
        newFilter.actionCode.keys.push("UNDEFINED");
        undefinedValues.actionCode = true;
      }

      if (delay.responsibility) {
        newFilter.responsibility.keys.push(delay.responsibility.name);
      } else if(!undefinedValues.responsibility) {
        newFilter.responsibility.keys.push("UNDEFINED");
        undefinedValues.responsibility = true;
      }
    });

    newFilter.areaGroup.keys = newFilter.areaGroup.keys.filter(distinct);
    newFilter.area.keys = newFilter.area.keys.filter(distinct);
    newFilter.equipment.keys = newFilter.equipment.keys.filter(distinct);
    newFilter.observation.keys = newFilter.observation.keys.filter(distinct);
    newFilter.reason.keys = newFilter.reason.keys.filter(distinct);
    newFilter.actionCode.keys = newFilter.actionCode.keys.filter(distinct);
    newFilter.responsibility.keys = newFilter.responsibility.keys.filter(distinct);

    // const newObj: IDictionary<string[]> = {};
    // keys.forEach(key => newObj[key] = [ NullValue ]);
    // setFilter(newObj);

    setFilter(newFilter);
  }, [ data ]);

  useEffect(() => {
    const compare = (delay: IDelayQMOS, property: string): boolean => {
      const entity = delay[property as keyof IDelayQMOS] as IBasicEntity | null;
      const filterData = filter[property as keyof IFilter];

      return !entity || filterData.selected.length === 0 || filterData.selected.find(name => name === "UNDEFINED" && entity === null ||  name === entity?.name) !== undefined;
    };

    setDataFiltered(data.filter(d =>
      compare(d, "areaGroup") &&
      compare(d, "area") &&
      compare(d, "equipment") &&
      compare(d, "observation") &&
      compare(d, "reason") &&
      compare(d, "actionCode") &&
      compare(d, "responsibility")
    ));
  }, [ filter ]);


  useEffect(() => {
    if (onRefresh && delayUpdated) {
      onRefresh(delayUpdated);
      setOpen(false);
    }
  }, [ delayUpdated ]);

  const onUpdateDelay = (delayHistoryID: number, areaID: number | null, equipmentID: number | null, observationID: number | null,
    reasonID: number | null, responsibilityID: number | null, actionCodeID: number | null, comment: string, editedBy: string) => {

    updateDelay({
      module: "delay",
      query: "updateDelaysHistoryById",
      payload: {
        qmos: true,
        delayHistoryID,
        comment,
        editedBy,
        areaID,
        equipmentID,
        observationID,
        reasonID,
        responsibilityID,
        actionCodeID
      } }).then();
  };

  const createSortHandler = (property: ColumnType) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, row: IDelayQMOS) => {
    setSelected(row);
  };

  const handleEditButton2 = (row: IDelayQMOS) => {
    setSelectedNewRow(row);
    setOpen(true);
  };


  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenCutDialog = (row: IDelayQMOS) => {
    setSelectedNewRow(row);
    setOpenCutDialog(true);
  };

  const onHandleFilterAccept = (selectedFilter: string[]): void => {
    setFilter({ ...filter, [dialogFilterData.key]: { ...filter[dialogFilterData.key], selected: selectedFilter } });
    setDialogFilterData({ ...dialogFilterData, open: false });
  };

  const handleCutDialogAccept = async (productionID: number | null, machineID: number, beginD: number, endD: number, delayHistoryID: number) => {

    const res = await insNewDelay({ module: "delay", query: "insDelaysHistory",
      payload: {
        qmos: true,
        beginDatetime: beginD / 1000,
        endDatetime: endD / 1000,
        productionID,
        machineID,
        delayHistoryID
      } } );

    if(res) {
      onRefreshAfterCut(res, delayHistoryID, beginD / 1000);
    }
    setOpenCutDialog(false);

  };

  const handleCloseCutDialog = () => {
    setOpenCutDialog(false);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hasProd = dataFiltered !== null && dataFiltered.length > 0 && dataFiltered[0].production !== null;

  return (
    <Fragment>
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true} size="small">
          <TableHead>
            <TableRow>
              {columns.filter(x => hasProd || !x.prodField).map((column) => {

                const hasFilter = column.filter && filter[column.id as keyof IFilter].selected.length > 0;
                let tooltip: string | ReactElement = column.label;
                if (hasFilter) {
                  tooltip = <div className="display-flex-column">
                    {filter[column.id as keyof IFilter].selected.map((t, i) =>
                      <strong key={`tooltip-${column.label}-${i}`}>{t}</strong>)}
                  </div>;
                }

                return (
                  <TableCell
                    className="table-header"
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth(hasProd),
                      width: column.width ? column.width(hasProd) : undefined
                    }}
                    sortDirection={orderBy === column.id ? order : false}>
                    <div className="display-flex-row">
                      {column.filter &&
                      <Tooltip
                        title={tooltip}>
                        <IconButton onClick={() => setDialogFilterData({
                          open: true,
                          key: column.id as keyof IFilter,
                          title: column.label
                        })}>
                          <FilterList fontSize="small" color={hasFilter ? "secondary" : "inherit"}/>
                        </IconButton>
                      </Tooltip>
                      }
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={createSortHandler(column.id as keyof IDelayQMOS)}>
                        {column.label}
                        {orderBy === column.id
                          ? <span className="cell-visuallyHidden">
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </span>
                          : null}
                      </TableSortLabel>
                    </div>
                  </TableCell>);
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataFiltered && stableSort<any>(dataFiltered, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: IDelayQMOS, index: number) => {
                // {dataFiltered && stableSort<any>(dataFiltered, getComparator(order, orderBy)).map((row: IDelayQMOS, index: number) => {

                const rowBackground = row.endDatetime === null
                  ? "#fff59d"
                  : row.areaGroup === null
                    ? "#ff9d9d"
                    : undefined;

                const cellStyle = {
                  color: rowBackground ? "#303030" : undefined
                };


                return (
                  <TableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}
                    onClick={(event) => handleClick(event, row)}
                    style={{ background: rowBackground }}
                    selected={row === selected}>
                    {columns.filter(x => hasProd || !x.prodField).map((column: IColumn) => {
                      if (column.id === "duration") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell" style={cellStyle}>
                            {row.duration && formatNumber(row.duration / 60, 2)}
                          </TableCell>
                        );
                      }

                      if (column.id === "factoryDay") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell"  style={cellStyle}>
                            {row.production ? formatDate(row.production.shiftHistory.factoryDay) : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "shift") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell" style={cellStyle}>
                            {row.production ? row.production.shiftHistory.name : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "crew") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell" style={cellStyle}>
                            {row.production ? row.production.crew.name : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "machine") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell" style={cellStyle}>
                            {row.production ? row.production.machine.name : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "areaGroup") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.areaGroup?.name}
                          </TableCell>
                        );
                      }

                      if (column.id === "area") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.area?.name}
                          </TableCell>
                        );
                      }

                      if (column.id === "equipment") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.equipment?.name}
                          </TableCell>
                        );
                      }

                      if (column.id === "observation") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.observation?.name}
                          </TableCell>
                        );
                      }
                      if (column.id === "reason") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.reason?.name}
                          </TableCell>
                        );
                      }
                      if (column.id === "responsibility") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.responsibility?.name}
                          </TableCell>
                        );
                      }

                      if (column.id === "actionCode") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} style={cellStyle}>
                            {row.actionCode?.name}
                          </TableCell>
                        );
                      }

                      // si la columna es editable se dibujan
                      if (column.id === "operationEdit") {
                        if(isEditable){
                          return (
                            <TableCell key={`${column.id}-${index}`} align={column.align}  className="table-cell" style={cellStyle}>

                              {row.production &&
                              <IconButton aria-label="edit" size={"small"} style={cellStyle}
                                onClick={() => handleOpenCutDialog(row)} disabled={row.endDatetime === null}>
                                <CallSplitIcon/>
                              </IconButton>
                              }

                              <IconButton aria-label="edit" size={"small"} style={cellStyle}
                                onClick={() => handleEditButton2(row)}>
                                <EditIcon/>
                              </IconButton>

                            </TableCell>
                          );
                        }

                        return null;
                      }

                      const value = row[column.id] as BaseType;

                      return (
                        <TableCell key={`${column.id}-${index}`} align={column.align}  className="table-cell" style={cellStyle}>
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            {emptyRows > 0 &&
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}/>
              </TableRow>
            }
          </TableBody>
        </Table>
        {!dataFiltered &&
        <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2"  >
          <CircularProgress className=""/>
        </Paper>
        }
      </TableContainer>

      <Table>
        <TableBody>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 1}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>

      <EditDelayDialog open={open} handleClose={handleClose} selectedDelay={selectedNewRow}
        updateDelay={onUpdateDelay} />

      <CutDelayDialog open={openCutDialog} handleClose={handleCloseCutDialog} selectedNewRow={selectedNewRow}
        handleAccept={handleCutDialogAccept}/>

      { dialogFilterData.open &&
        <FilterDialog open={dialogFilterData.open}
          handleClose={() => setDialogFilterData({ ...dialogFilterData, open: false })}
          onHandleAccept={onHandleFilterAccept} property={dialogFilterData.title}
          keys={filter[dialogFilterData.key].keys} currentFilter={filter[dialogFilterData.key].selected}/> }
    </Fragment>
  );
};

export default DelayTable;
